/* eslint-disable no-prototype-builtins */

import { QTY_MAX_VALUE } from "@eisinc/XDL/constants";
import breakpoints from "@eisinc/XDL/PreStyle/breakpoints";

/**
 * XNGAGE CONFIDENTIAL
 * __________________________
 *
 * Copyright (C) 2020 Xngage - All Rights Reserved
 *
 * All code or information contained herein is, and remains the
 * property of Xngage LLC and its customers.  The intellectual
 * and technical concepts contained are proprietary to Xngage LLC
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Xngage LLC.
 */
export const prepareSiteMessageWithLink = (messageText: any, linkText: any, linkValue: any, argument: any) => {
    return messageText?.toString()?.replace(argument, `<a href="${linkValue}">${linkText.toString()}</a>`);
};

export const handleSiteMessageLinkClick = (e: any, callback: any) => {
    e.preventDefault();

    let el = e.target;

    while (el && el !== e.currentTarget && el.tagName !== "A") {
        el = el.parentNode;
    }

    if (el && el.tagName === "A") {
        callback && callback();
    }
};

export const doScroll = (scrollToTop = false) => {
    setTimeout(() => {
        const selector = document.querySelector("[aria-invalid='true']");

        if (selector) {
            selector.scrollIntoView({
                behavior: "smooth",
                block: "center",
            });

            return;
        }

        if (scrollToTop) {
            window.scrollTo(0, 0);
            return;
        }

        doScroll(scrollToTop);
    }, 500);
};

export const isProductInActive = (product: any) => {
    return (
        (product?.hasOwnProperty("isActive") && !product.isActive) ||
        (product?.hasOwnProperty("isActiveProduct") && !(product as any).isActiveProduct)
    );
};

export const isMobileWidth = () => window.outerWidth < 768;

export const getPathFromUrl = (url: any) => {
    return url.split(/[?#]/)[0];
};

export const checkPostalCode = (country: string, postalCode: string) => {
    let error = false;

    switch (country) {
        case "US":
        case "MX":
            if (!/^\d{5}(?:[-\s]\d{4})?$/.test(postalCode)) {
                error = true;
            }
            break;
        case "CA":
            if (!/^[ABCEGHJKLMNPRSTVXY]\d[ABCEGHJKLMNPRSTVWXYZ]( )\d[ABCEGHJKLMNPRSTVWXYZ]\d$/i.test(postalCode)) {
                error = true;
            }
            break;
    }

    return error;
};

export const prepareAddressForValidation = (address: any) => {
    let country = "";
    let state = "";

    if (address.country) {
        if (typeof address.country === "string") {
            country = address.country;
        } else {
            country = address.country?.abbreviation;
        }
    }

    if (address.state) {
        if (typeof address.state === "string") {
            state = address.state;
        } else {
            state = address.state?.abbreviation;
        }
    }

    return {
        addressLine1: address.address1 || "",
        addressLine2: address.address2 || "",
        addressLine3: address.address3 || "",
        city: address.city,
        country,
        postalCode: address.postalCode,
        state,
    };
};

export const isMobileScreen = () => typeof window !== "undefined" && window.matchMedia("(max-width: 768px)").matches;

export const isTabletScreen = () => typeof window !== "undefined" && window.matchMedia("(max-width: 1024px)").matches;

export const isWideScreen = () => typeof window !== "undefined" && window.matchMedia("(min-width: 2200px)").matches;

/**
 *
 * we have used this equation vw=(fontSize/viewportWidth) × 100 to adjust font size on tablet screen
 * @param mobile
 * @param tablet
 * @param desktop
 * @returns
 */
export const autoClamp = (mobile: number, tablet: number, desktop: number) => {
    const vw = ((tablet / breakpoints.values[2]) * 100).toFixed(2);
    return `clamp(${mobile}px ,${vw}vw , ${desktop}px)`;
};

export const getValidatedQty = (value: number, minQty: number, msq: number) => {
    let newValue = value;
    if (newValue <= 0 || newValue > QTY_MAX_VALUE) {
        newValue = minQty > 0 ? minQty : 1;
    }

    if (newValue < minQty) {
        newValue = minQty;
    }

    if (msq > 0 && newValue % msq !== 0) {
        const isFractionalMsq = !Number.isInteger(msq);
        if (isFractionalMsq) {
            const remainder = newValue % msq;
            if (remainder !== 0) {
                newValue = newValue - remainder + msq;
            }
        } else {
            newValue = Math.ceil(newValue / msq) * msq;
        }
    }
    return newValue;
};

/**
 * Creates a promise that resolves after a specified delay.
 *
 * This utility is often used to pause execution in async functions,
 * simulate network latency, or delay operations for UX effects (e.g. loading animations).
 *
 * @param {number} [time=100] - The delay duration in milliseconds before the promise resolves.
 * @returns {Promise<boolean>} A promise that resolves to `true` after the given delay.
 */

export const delay = (time = 100) => {
    return new Promise(resolve => {
        setTimeout(() => {
            resolve(true);
        }, time);
    });
};

/**
 * To convert string to boolean
 * @param value
 */
export const parseBoolean = (value?: string) => (value ? value?.toLowerCase() === "true" : false);

/**
 * Detects if the current browser is Safari on desktop (excludes Chrome and iOS devices)
 * @returns true if the browser is Safari on desktop, false otherwise
 */
export const isSafariDesktop = () => {
    const checkSafari = /(\bChrome\b|\bSafari\b)\//.exec(navigator.userAgent);
    return checkSafari != null && checkSafari[1] !== "Chrome" && /\biPod\b|\biPad\b/g.exec(navigator.userAgent) == null;
};

/**
 * Detects if the current browser is Firefox
 * @returns true if the browser is Firefox, false otherwise
 */
export const isFirefox = () => {
    const checkFirefox = /(\bFirefox\b)\//.exec(navigator.userAgent);
    return checkFirefox !== null && checkFirefox[1] === "Firefox";
};
